import { DrillPageGlyphBlock } from "@-/dev.improve.haniverse.routes.nav.app.learn.drill._components.glyph-block";
import {
  DrillPageGlyphPrompt,
  useDrillPageGlyphPromptGate,
} from "@-/dev.improve.haniverse.routes.nav.app.learn.drill._components.glyph-prompt";
import { DrillPageHeaderTexts } from "@-/dev.improve.haniverse.routes.nav.app.learn.drill._components.header-texts";
import { DrillPageWordSentencePrompt } from "@-/dev.improve.haniverse.routes.nav.app.learn.drill._components.word-sentence-prompt";
import { DrillPageSentencePrompt } from "@-/dev.improve.haniverse.routes.nav.app.learn.drill._components.sentence-prompt";
import {
  DrillPagePronunciationHint,
  useDrillPagePronunciationHintGate,
} from "@-/dev.improve.haniverse.routes.nav.app.learn.drill._components.pronunciation-hint";
import { DrillPageContainer } from "@-/dev.improve.haniverse.routes.nav.app.learn.drill._components.container";
import { DrillPageGlyphStyleToggle } from "@-/dev.improve.haniverse.routes.nav.app.learn._components.glyph-style-toggle";
import { useDrillPageKeyboardAwareContentStyle } from "@-/dev.improve.haniverse.routes.nav.app.learn.drill._lib.keyboard-aware-content-style";
import {
  DrillPageContext,
  GraphemeDrillContext,
} from "@-/dev.improve.haniverse.routes.nav.app.learn.drill._context";
import { View } from "@-/com.npmjs.react-native";
import { useKeyboardState } from "@-/com.npmjs.react-native-keyboard-controller";
import Animated from "@-/com.npmjs.react-native-reanimated";
import { useContextSelectors } from "@-/com.npmjs.use-context-selectors";

/** The reading hint leads, with the glyph as a smaller reference underneath. */
function PronunciationPortraitLayout({ pinyin }: { pinyin: string }) {
  const pronunciationHintGate = useDrillPagePronunciationHintGate(pinyin);
  return (
    <DrillPageContainer
      headerCenter={<DrillPageHeaderTexts section="heading" variant="portrait" />}
      headerEnd={<DrillPageGlyphStyleToggle position="card-header" />}
    >
      {/* Keep the reading hint central even while the pinyin keyboard is open. */}
      <View className="min-h-0 flex-1 flex-col px-2.25 pb-2.25" pointerEvents="none">
        {pronunciationHintGate === null ? (
          <>
            <View className="shrink-0">
              <DrillPageHeaderTexts section="prompt" variant="portrait" />
            </View>
            <DrillPageGlyphBlock />
          </>
        ) : (
          <>
            <View className="min-h-0 flex-1 items-center justify-center px-3">
              <DrillPageHeaderTexts section="prompt" variant="portrait" />
              <DrillPagePronunciationHint gate={pronunciationHintGate} variant="portrait" />
            </View>
            <View className="h-20 shrink-0 items-center pt-2">
              <DrillPageGlyphBlock />
            </View>
          </>
        )}
      </View>
    </DrillPageContainer>
  );
}

function MeaningPortraitLayout() {
  return (
    <DrillPageContainer
      headerCenter={<DrillPageHeaderTexts section="heading" variant="portrait" />}
      headerEnd={<DrillPageGlyphStyleToggle position="card-header" />}
    >
      {/* The recall question sits above the glyph so the prompt is read first. */}
      <View className="min-h-0 flex-1 flex-col px-2.25 pb-2.25" pointerEvents="none">
        <View className="shrink-0">
          <DrillPageHeaderTexts section="prompt" variant="portrait" />
        </View>
        <View className="min-h-0 flex-1">
          <DrillPageGlyphBlock />
        </View>
      </View>
    </DrillPageContainer>
  );
}

/** A grapheme drill's portrait front. */
export function GraphemeDrillPagePortraitView() {
  const [drillType] = useContextSelectors(GraphemeDrillContext, (c) => c.drillType);
  return drillType.kind === "pronunciation" ? (
    <PronunciationPortraitLayout pinyin={drillType.pinyin} />
  ) : (
    <MeaningPortraitLayout />
  );
}

/** A word drill's portrait front: the sentence prompt, or the sentence being produced. */
export function WordDrillPagePortraitView() {
  const { keyboardAwareContentStyle } = useDrillPageKeyboardAwareContentStyle();
  const [isShuangpinKeyboardVisible, sentenceDrill] = useContextSelectors(
    DrillPageContext,
    (c) => c.isShuangpinKeyboardVisible,
    (c) => c.sentenceDrill,
  );
  const isOsKeyboardVisible = useKeyboardState((s) => s.isVisible);
  const glyphPromptGate = useDrillPageGlyphPromptGate();

  // A keyboard shortens the content area enough that a *centered* column would push the header up
  // under the app header and clip it. In that case anchor the column to the top so overflow spills
  // downward (behind the keyboard) and the header never clips. With no keyboard, keep the content
  // centered.
  const isKeyboardOpen = isShuangpinKeyboardVisible || isOsKeyboardVisible;
  const contentAlignmentClassName = isKeyboardOpen ? "justify-start pt-4" : "justify-center";

  // With a keyboard open there isn't room for both the header/prompt AND the lower hint lines
  // above the keyboard, so hide the optional hints then (the input is right there) and keep the
  // header fully visible. They return once the keyboard is dismissed.
  const areHintsHidden = isKeyboardOpen;

  return (
    <DrillPageContainer>
      <View className="min-h-0 flex-1" pointerEvents="box-none">
        <DrillPageGlyphStyleToggle />
        <Animated.View
          className={`z-10 flex-1 items-center px-6 ${contentAlignmentClassName}`}
          pointerEvents="none"
          style={keyboardAwareContentStyle}
        >
          <DrillPageHeaderTexts variant="portrait" />
          {/* A sentence drill's answer is the whole sentence, built up in place: the English
              translation is the prompt and the strip below it is what the user fills in. A word
              card's glyph IS the answer, so it shows the translation + pinyin prompt instead. */}
          {sentenceDrill === null ? (
            <DrillPageWordSentencePrompt variant="portrait" />
          ) : (
            <DrillPageSentencePrompt sentenceDrill={sentenceDrill} variant="portrait" />
          )}
          {!areHintsHidden && glyphPromptGate !== null && (
            <DrillPageGlyphPrompt gate={glyphPromptGate} variant="portrait" />
          )}
        </Animated.View>
      </View>
    </DrillPageContainer>
  );
}
